
import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList } from 'recharts';

// Below the lg breakpoint — recharts takes numeric props, so Tailwind prefixes can't drive this
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

interface ReportChartsProps {
  reportType: string;
  data: any;
}

const COLORS = ['#1e3a5f', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// Custom label for radial chart - inside with white text
const renderCustomizedLabel = (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Only show label if percentage is significant enough
  if (percent < 0.05) return null;

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor="middle" 
      dominantBaseline="central" 
      className="text-sm font-bold"
      style={{ 
        textShadow: '0 1px 3px rgba(0,0,0,0.5)',
        pointerEvents: 'none'
      }}
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};

// Custom tooltip for better display
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-xl border-2 border-gray-200">
        <p className="font-semibold text-gray-900">{payload[0].name}</p>
        <p className="text-sm text-gray-600">
          Value: <span className="font-bold text-brand-600">{payload[0].value.toLocaleString()}</span>
        </p>
      </div>
    );
  }
  return null;
};

export default function ReportCharts({ reportType, data }: ReportChartsProps) {
  const summary = data.summary || {};
  const isMobile = useIsMobile();

  const getChartData = () => {
    switch (reportType) {
      case 'inventory':
        // Top clients by quantity (bar chart)
        const topClients = summary.topClientsByQuantity || [];
        
        // Category distribution (donut chart)
        const categoryDist = summary.categoryDistribution || [];

        return { 
          barChart: topClients.slice(0, 10), 
          donutChart: categoryDist.slice(0, 8),
          barDataKey: 'totalQty',
          barLabel: 'Top Clients by Quantity',
          donutLabel: 'Category Distribution'
        };

      case 'inward':
        const inwardClients = summary.topClientsByPallets || [];
        const inwardStatus = summary.statusDistribution || [];

        return { 
          barChart: inwardClients.slice(0, 10),
          donutChart: inwardStatus,
          barDataKey: 'totalPallets',
          barLabel: 'Inbound by Client (Pallets)',
          donutLabel: 'Status Distribution'
        };

      case 'outward':
        const outwardClients = summary.topClientsByPallets || [];
        const outwardStatus = summary.statusDistribution || [];

        return { 
          barChart: outwardClients.slice(0, 10),
          donutChart: outwardStatus,
          barDataKey: 'totalPallets',
          barLabel: 'Outbound by Client (Pallets)',
          donutLabel: 'Status Distribution'
        };

      case 'damage':
        const damageClients = summary.topClientsByDamage || [];
        const damageTypes = summary.damageTypeDistribution || [];

        return { 
          barChart: damageClients.slice(0, 10),
          donutChart: damageTypes,
          barDataKey: 'totalDamaged',
          barLabel: 'Damage by Client',
          donutLabel: 'Damage Type Distribution'
        };

      case 'client-utilization':
        const utilization = summary.utilizationByClient || [];
        const utilizationRanges = summary.utilizationRanges || [];

        return { 
          barChart: utilization.slice(0, 10),
          donutChart: utilizationRanges,
          barDataKey: 'utilization',
          barLabel: 'Client Utilization (%)',
          donutLabel: 'Utilization Ranges'
        };

      case 'additional-space':
        const topClientsBySpace = summary.topClientsBySpace || [];
        const statusDistribution = summary.statusDistribution || [];

        return { 
          barChart: topClientsBySpace.slice(0, 10),
          donutChart: statusDistribution,
          barDataKey: 'totalSpace',
          barLabel: 'Top Clients by Additional Space (Sqft)',
          donutLabel: 'Status Distribution'
        };

      default:
        return { barChart: null, donutChart: null, barDataKey: '', barLabel: '', donutLabel: '' };
    }
  };

  const { barChart, donutChart, barDataKey, barLabel, donutLabel } = getChartData();

  if (!barChart && !donutChart) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      {/* Bar Chart */}
      {barChart && barChart.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-300 shadow-xl transform transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center pb-4 border-b-2 border-gray-200">
            <div className="p-2 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg mr-3 shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span className="bg-gradient-to-r from-brand-600 to-brand-700 bg-clip-text text-transparent">{barLabel}</span>
          </h3>
          {isMobile ? (
            /* Horizontal bars on mobile - one full-width row per client, no label collisions */
            <ResponsiveContainer width="100%" height={barChart.length * 42 + 20}>
                <BarChart
                  data={barChart}
                  layout="vertical"
                  margin={{ top: 5, right: 45, left: 0, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="barGradientMobile" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#1e3a5f" stopOpacity={1}/>
                      <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={105}
                    interval={0}
                    tick={{ fontSize: 10, fill: '#4b5563', fontWeight: 500 }}
                    tickFormatter={(value: string) =>
                      value && value.length > 15 ? `${value.slice(0, 14)}…` : value
                    }
                    stroke="#d1d5db"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey={barDataKey}
                    fill="url(#barGradientMobile)"
                    radius={[0, 6, 6, 0]}
                    barSize={20}
                    animationDuration={1000}
                    animationBegin={0}
                  >
                    <LabelList
                      dataKey={barDataKey}
                      position="right"
                      style={{ fontSize: '10px', fontWeight: 'bold', fill: '#1f2937' }}
                    />
                  </Bar>
                </BarChart>
            </ResponsiveContainer>
          ) : (
          <ResponsiveContainer width="100%" height={340}>
            <BarChart
              data={barChart}
              margin={{ top: 20, right: 30, left: 20, bottom: 90 }}
            >
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1e3a5f" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.8}/>
                </linearGradient>
              </defs>
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fontSize: 11, fill: '#4b5563', fontWeight: 500 }}
                interval={0}
                stroke="#d1d5db"
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#4b5563', fontWeight: 500 }}
                stroke="#d1d5db"
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey={barDataKey}
                fill="url(#barGradient)"
                radius={[8, 8, 0, 0]}
                animationDuration={1000}
                animationBegin={0}
              >
                <LabelList
                  dataKey={barDataKey}
                  position="top"
                  style={{ fontSize: '11px', fontWeight: 'bold', fill: '#1f2937' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Donut Chart */}
      {donutChart && donutChart.length > 0 && (
        <div className="report-donut-chart bg-white rounded-2xl p-6 border-2 border-gray-300 shadow-xl transform transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center pb-4 border-b-2 border-gray-200">
            <div className="p-2 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg mr-3 shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
            </div>
            <span className="bg-gradient-to-r from-brand-600 to-brand-700 bg-clip-text text-transparent">{donutLabel}</span>
          </h3>
          <ResponsiveContainer width="100%" height={isMobile ? 240 : 340}>
            <PieChart>
              <Pie
                data={donutChart}
                cx="50%"
                cy="42%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                innerRadius={60}
                fill="#8884d8"
                dataKey="value"
                paddingAngle={2}
                animationBegin={0}
                animationDuration={800}
                animationEasing="ease-out"
              >
                {donutChart.map((entry: any, index: number) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                    stroke="#fff"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="bottom" 
                height={70}
                wrapperStyle={{ paddingTop: '10px' }}
                formatter={(value, entry: any) => (
                  <span className="text-xs font-medium text-gray-700">
                    {value}: <span className="font-bold text-gray-900">{entry.payload.value.toLocaleString()}</span>
                  </span>
                )}
                iconType="circle"
                iconSize={10}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Mobile legend - normal flow so it can never overlap content below */}
          <div className="lg:hidden flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2">
            {donutChart.map((entry: any, index: number) => (
              <span key={index} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                ></span>
                {entry.name}: <span className="font-bold text-gray-900">{entry.value?.toLocaleString()}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
